import { z, type ZodType } from "zod";

import {
  RetailDashboardSnapshot,
  RetailSidebarSnapshot,
} from "@/features/retail/types/dashboard";
import {
  RetailAssociate,
  RetailCustomerSuggestion,
  RetailInventoryLevel,
  RetailProduct,
  RetailRegister,
  RetailTransaction,
} from "@/features/retail/types/datasets";
import { ApiResponseError, assertOk, parseJson } from "./response";

type ApiFetchFn = typeof import("./requests")["apiFetch"];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === "function") {
    try {
      const mockedModule = jestGlobal.requireMock("@/lib/api/requests") as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === "function") {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import("./requests");
  return module.apiFetch;
};

const RETAIL_API_BASE = "/api/retail";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

type RetailRequestOptions = {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  tenantSlug?: string;
};

const hasHeader = (headers: Record<string, string>, name: string): boolean =>
  Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase());

const createRetailResponseSchema = <T>(schema: ZodType<T>) =>
  z
    .object({
      ok: z.literal(true),
      data: schema,
    })
    .passthrough();

const serialiseBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined || body === null) {
    return undefined;
  }
  if (typeof body === "string") {
    return body;
  }
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    return body;
  }
  if (typeof Blob !== "undefined" && body instanceof Blob) {
    return body;
  }
  if (body instanceof URLSearchParams) {
    return body;
  }
  return JSON.stringify(body);
};

const isJsonStringBody = (body: BodyInit | undefined): body is string => typeof body === "string";

const callRetailApi = async <T>(
  path: string,
  schema: ZodType<T>,
  { method = "GET", body, signal, headers = {}, tenantSlug }: RetailRequestOptions = {},
): Promise<T> => {
  if (typeof window === "undefined") {
    throw new ApiResponseError(
      `Retail API request for ${path} is unavailable in the current environment.`,
      503,
    );
  }

  const apiFetch = await resolveApiFetch();

  const requestHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };

  if (tenantSlug && !hasHeader(requestHeaders, "x-tenant")) {
    requestHeaders["X-Tenant"] = tenantSlug;
  }

  const serialisedBody = serialiseBody(body);

  if (serialisedBody !== undefined && isJsonStringBody(serialisedBody) && !hasHeader(requestHeaders, "content-type")) {
    requestHeaders["Content-Type"] = "application/json";
  }

  const response = await apiFetch(path, {
    method,
    signal,
    headers: requestHeaders,
    body: serialisedBody,
  });

  await assertOk(response);
  const payload = await parseJson(response, createRetailResponseSchema(schema));
  return payload.data;
};

const RetailProductVariantSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    sku: z.string(),
    barcode: z.string().nullable().optional(),
    priceCents: z.number(),
    unitCostCents: z.number(),
    options: z.record(z.string()),
  })
  .passthrough();

const RetailProductSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    sku: z.string(),
    barcode: z.string().nullable().optional(),
    category: z.string(),
    subcategory: z.string().nullable().optional(),
    unitCostCents: z.number(),
    defaultPriceCents: z.number(),
    variants: z.array(RetailProductVariantSchema),
    isActive: z.boolean(),
    reorderPoint: z.number().nullable().optional(),
    safetyStock: z.number().nullable().optional(),
    supplierName: z.string().nullable().optional(),
    tags: z.array(z.string()),
  })
  .passthrough();

const InventoryUnitDefinitionSnapshotSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    abbreviation: z.string(),
    family: z.string(),
    baseUnit: z.string(),
    toBaseFactor: z.number(),
  })
  .passthrough();

const InventoryClassificationSnapshotSchema = z
  .object({
    kind: z.string(),
    unitId: z.string(),
    baseUnitId: z.string(),
    displayUnitId: z.string(),
    unit: InventoryUnitDefinitionSnapshotSchema,
    baseUnit: InventoryUnitDefinitionSnapshotSchema,
    displayUnit: InventoryUnitDefinitionSnapshotSchema,
    unitOverrides: z.record(z.number()),
    unitSize: z.number(),
  })
  .passthrough();

const RetailInventoryLevelSchema = z
  .object({
    id: z.string(),
    productId: z.string(),
    variantId: z.string().nullable().optional(),
    locationId: z.string().nullable().optional(),
    locationName: z.string().nullable().optional(),
    onHand: z.number(),
    allocated: z.number(),
    onOrder: z.number(),
    reorderPoint: z.number().nullable().optional(),
    safetyStock: z.number().nullable().optional(),
    lastReceivedAt: z.string().nullable(),
    nextDeliveryEta: z.string().nullable(),
    updatedAt: z.string(),
    metadata: z.record(z.unknown()).nullable().optional(),
    classification: InventoryClassificationSnapshotSchema.nullable().optional(),
  })
  .passthrough();

const RetailCustomerSuggestionSchema = z
  .object({
    id: z.string(),
    name: z.string().nullable().optional(),
    email: z.string().email().nullable().optional(),
    phone: z.string().nullable().optional(),
    loyaltyId: z.string().nullable().optional(),
    loyaltyTier: z.string().nullable().optional(),
    spendToDateCents: z.number(),
    lastVisitAt: z.string().nullable().optional(),
    visitCount: z.number().int().nonnegative().optional(),
  })
  .transform<RetailCustomerSuggestion>((value) => ({
    id: value.id,
    name: value.name ?? null,
    email: value.email ?? null,
    phone: value.phone ?? null,
    loyaltyId: value.loyaltyId ?? null,
    loyaltyTier: value.loyaltyTier ?? null,
    spendToDateCents: value.spendToDateCents,
    lastVisitAt: value.lastVisitAt ?? null,
    visitCount: value.visitCount ?? 0,
  }));

const RetailCustomerSuggestionsResponseSchema = z
  .object({
    customers: z.array(RetailCustomerSuggestionSchema),
  })
  .passthrough();

const RetailTenderSchema = z
  .object({
    method: z.enum(["cash", "card", "gift", "store_credit"]),
    amountCents: z.number(),
    externalReference: z.string().nullable().optional(),
  })
  .passthrough();

const RetailTransactionLineSchema = z
  .object({
    id: z.string().optional(),
    kind: z.enum(["catalog", "service"]),
    productId: z.string().nullable(),
    variantId: z.string().nullable().optional(),
    quantity: z.number(),
    unitPriceCents: z.number(),
    discountCents: z.number(),
    taxPolicy: z.enum(["default", "override", "exempt"]).optional(),
    taxRateOverride: z.number().nullable().optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .passthrough();

const RetailTransactionSchema = z
  .object({
    id: z.string(),
    receiptNumber: z.string(),
    channel: z.enum(["pos", "bopis", "ship"]),
    status: z.enum(["completed", "pending", "voided", "refunded"]),
    createdAt: z.string(),
    associateId: z.string().nullable().optional(),
    subtotalCents: z.number(),
    taxCents: z.number(),
    totalCents: z.number(),
    tenders: z.array(RetailTenderSchema),
    lines: z.array(RetailTransactionLineSchema),
    customerProfileId: z.string().nullable().optional(),
    loyaltyEmail: z.string().nullable().optional(),
    customerName: z.string().nullable().optional(),
    loyaltyPointsAwarded: z.number().optional(),
    loyaltyPointsRedeemed: z.number().optional(),
    loyaltyProgramSnapshot: z.record(z.unknown()).nullable().optional(),
    loyaltyOptIn: z.boolean().optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .passthrough();

const RetailAssociateSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    role: z.enum(["associate", "lead", "manager"]),
    isActive: z.boolean(),
    shiftStartedAt: z.string().nullable(),
    shiftEndsAt: z.string().nullable(),
    registersAssigned: z.array(z.string()),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

const RetailRegisterSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    status: z.enum(["online", "offline", "maintenance"]),
    locationId: z.string().nullable().optional(),
    locationName: z.string().nullable().optional(),
    lastSyncedAt: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

const RetailCheckoutSnapshotSchema = z
  .object({
    associates: z.array(RetailAssociateSchema),
    registers: z.array(RetailRegisterSchema),
    transactions: z.array(RetailTransactionSchema),
  })
  .passthrough();

const RetailMetricSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    description: z.string(),
    value: z.string(),
    delta: z
      .object({
        value: z.string(),
        direction: z.enum(["up", "down", "flat"]),
      })
      .optional(),
  })
  .passthrough();

const RetailTaskSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    status: z.enum(["open", "in_progress", "completed"]),
    dueAt: z.string().nullable().optional(),
    priority: z.enum(["low", "medium", "high"]),
  })
  .passthrough();

const RetailTopPerformerSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    category: z.string(),
    salesValue: z.number(),
    salesUnit: z.number(),
    stockOnHand: z.number(),
    stockStatus: z.enum(["healthy", "low", "out"]),
  })
  .passthrough();

const RetailTrafficPointSchema = z
  .object({
    hour: z.string(),
    transactions: z.number(),
    revenueCents: z.number(),
  })
  .passthrough();

const RetailSalesChannelSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    abbreviation: z.string(),
    icon: z.string(),
    mixPercent: z.number(),
    trend: z.enum(["up", "down", "flat"]),
  })
  .passthrough();

const RetailDashboardSnapshotSchema = z
  .object({
    asOf: z.string(),
    dateRangeLabel: z.string(),
    summaryMetrics: z.array(RetailMetricSchema),
    openTasks: z.array(RetailTaskSchema),
    topPerformers: z.array(RetailTopPerformerSchema),
    trafficSeries: z.array(RetailTrafficPointSchema),
    channelMix: z.array(RetailSalesChannelSchema),
  })
  .passthrough();

const RetailSidebarSnapshotSchema = z
  .object({
    name: z.string(),
    status: z.enum(["online", "offline"]),
    statusLabel: z.string(),
    activeAssociates: z.number(),
    totalAssociates: z.number(),
    registerCount: z.number(),
    uptimePercent: z.number(),
    isDemo: z.boolean(),
    lastSyncedAt: z.string().nullable(),
  })
  .passthrough();

const RetailInventoryLedgerEntrySchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    levelId: z.string(),
    eventType: z.enum([
      "receipt",
      "adjustment",
      "sale",
      "cycle_count",
      "correction",
      "prep",
      "waste",
      "transfer",
      "spoilage",
    ]),
    quantityDelta: z.number(),
    resultingOnHand: z.number(),
    reference: z.string().nullable(),
    note: z.string().nullable(),
    source: z.record(z.unknown()),
    occurredAt: z.string(),
    createdAt: z.string(),
  })
  .passthrough();

const LevelWithLedgerSchema = z
  .object({
    level: RetailInventoryLevelSchema,
    ledgerEntry: RetailInventoryLedgerEntrySchema,
  })
  .passthrough();

const RetailInventoryTransferResponseSchema = z
  .object({
    source: LevelWithLedgerSchema,
    target: LevelWithLedgerSchema,
  })
  .passthrough();

const RetailCycleCountTaskSchema = z
  .object({
    id: z.string(),
    levelId: z.string(),
    productId: z.string(),
    productName: z.string(),
    productSku: z.string(),
    locationId: z.string().nullable(),
    locationName: z.string().nullable(),
    status: z.enum(["scheduled", "in_progress", "completed"]),
    requestedAt: z.string(),
    completedAt: z.string().nullable().optional(),
    note: z.string().nullable(),
    expectedOnHand: z.number(),
  })
  .passthrough();

const RetailTransactionCreatedSchema = RetailTransactionSchema.extend({
  metadata: z.record(z.unknown()).optional(),
}).passthrough();

const cloneProduct = (product: RetailProduct): RetailProduct => ({
  ...product,
  variants: product.variants.map((variant) => ({
    ...variant,
    options: { ...variant.options },
  })),
  tags: [...product.tags],
});

const cloneTransaction = (transaction: RetailTransaction): RetailTransaction => ({
  ...transaction,
  loyaltyProgramSnapshot:
    transaction.loyaltyProgramSnapshot && typeof transaction.loyaltyProgramSnapshot === "object"
      ? { ...(transaction.loyaltyProgramSnapshot as Record<string, unknown>) }
      : transaction.loyaltyProgramSnapshot ?? null,
  metadata: transaction.metadata ? { ...transaction.metadata } : undefined,
  tenders: transaction.tenders.map((tender) => ({ ...tender })),
  lines: transaction.lines.map((line) => ({
    ...line,
    kind: line.kind ?? (line.productId ? "catalog" : "service"),
    taxPolicy: line.taxPolicy ?? "default",
    taxRateOverride:
      typeof line.taxRateOverride === "number" ? line.taxRateOverride : null,
    metadata: line.metadata ? { ...line.metadata } : undefined,
  })),
});

const cloneAssociate = (associate: RetailAssociate): RetailAssociate => ({
  ...associate,
  registersAssigned: [...associate.registersAssigned],
});

const cloneRegister = (register: RetailRegister): RetailRegister => ({ ...register });

export interface FetchRetailDatasetOptions {
  signal?: AbortSignal;
}

export const fetchRetailCatalog = async (
  options: FetchRetailDatasetOptions,
): Promise<RetailProduct[]> => {
  const products = await callRetailApi(
    `${RETAIL_API_BASE}/catalog`,
    z.array(RetailProductSchema),
    {
      signal: options.signal,
    },
  );
  return products.map(cloneProduct);
};

export const fetchRetailInventory = async (
  options: FetchRetailDatasetOptions,
): Promise<RetailInventoryLevel[]> => {
  const inventory = await callRetailApi(
    `${RETAIL_API_BASE}/inventory`,
    z.array(RetailInventoryLevelSchema),
    { signal: options.signal },
  );
  return inventory.map((entry) => ({ ...entry }));
};

export const fetchRetailTransactions = async (
  options: FetchRetailDatasetOptions,
): Promise<RetailTransaction[]> => {
  const data = await callRetailApi(
    `${RETAIL_API_BASE}/checkout`,
    RetailCheckoutSnapshotSchema,
    { signal: options.signal },
  );
  return data.transactions.map(cloneTransaction);
};

export const fetchRetailAssociates = async (
  options: FetchRetailDatasetOptions,
): Promise<RetailAssociate[]> => {
  const data = await callRetailApi(
    `${RETAIL_API_BASE}/checkout`,
    RetailCheckoutSnapshotSchema,
    { signal: options.signal },
  );
  return data.associates.map(cloneAssociate);
};

export const fetchRetailCustomers = async (options: {
  search?: string;
  limit?: number;
  signal?: AbortSignal;
} = {}): Promise<RetailCustomerSuggestion[]> => {
  const params = new URLSearchParams();
  const trimmedSearch = options.search?.trim();
  if (trimmedSearch) {
    params.set("search", trimmedSearch);
  }
  if (typeof options.limit === "number" && Number.isFinite(options.limit)) {
    params.set("limit", String(Math.max(1, Math.min(Math.trunc(options.limit), 25))));
  }
  const query = params.toString();

  const payload = await callRetailApi(
    `${RETAIL_API_BASE}/customers${query.length > 0 ? `?${query}` : ""}`,
    RetailCustomerSuggestionsResponseSchema,
    { signal: options.signal },
  );

  return payload.customers;
};

export const fetchRetailRegisters = async (
  options: FetchRetailDatasetOptions,
): Promise<RetailRegister[]> => {
  const data = await callRetailApi(
    `${RETAIL_API_BASE}/checkout`,
    RetailCheckoutSnapshotSchema,
    { signal: options.signal },
  );
  return data.registers.map(cloneRegister);
};

export interface FetchRetailDashboardOptions extends FetchRetailDatasetOptions {
  tenantId?: string | null;
}

export const fetchRetailDashboard = async (
  options: FetchRetailDashboardOptions,
): Promise<RetailDashboardSnapshot> => {
  return callRetailApi(
    `${RETAIL_API_BASE}/dashboard`,
    RetailDashboardSnapshotSchema,
    { signal: options.signal },
  );
};

export interface FetchRetailSidebarOptions extends FetchRetailDatasetOptions {
  tenantId?: string | null;
}

export const fetchRetailSidebarSnapshot = async (
  options: FetchRetailSidebarOptions,
): Promise<RetailSidebarSnapshot> => {
  return callRetailApi(
    `${RETAIL_API_BASE}/sidebar`,
    RetailSidebarSnapshotSchema,
    { signal: options.signal },
  );
};

export interface RetailInventoryAdjustmentPayload {
  levelId: string;
  delta: number;
}

export interface RetailInventoryLedgerEntry {
  id: string;
  tenantId: string;
  levelId: string;
  eventType:
    | "receipt"
    | "adjustment"
    | "sale"
    | "cycle_count"
    | "correction"
    | "prep"
    | "waste"
    | "transfer"
    | "spoilage";
  quantityDelta: number;
  resultingOnHand: number;
  reference: string | null;
  note: string | null;
  source: Record<string, unknown>;
  occurredAt: string;
  createdAt: string;
}

export const submitRetailInventoryAdjustment = async (
  payload: RetailInventoryAdjustmentPayload,
): Promise<RetailInventoryLevel> => {
  const response = await callRetailApi(
    `${RETAIL_API_BASE}/inventory/adjust`,
    LevelWithLedgerSchema,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return { ...response.level };
};

export interface RetailInventoryReceiptPayload {
  levelId: string;
  quantity: number;
  reference?: string | null;
  note?: string | null;
  expectedQuantity?: number | null;
}

export const submitRetailInventoryReceipt = async (
  payload: RetailInventoryReceiptPayload,
): Promise<RetailInventoryLevel> => {
  const response = await callRetailApi(
    `${RETAIL_API_BASE}/inventory/receive`,
    LevelWithLedgerSchema,
    {
      method: "POST",
      body: JSON.stringify({
        levelId: payload.levelId,
        quantity: payload.quantity,
        reference: payload.reference ?? null,
        note: payload.note ?? null,
        expectedQuantity: payload.expectedQuantity ?? null,
      }),
    },
  );
  return { ...response.level };
};

export interface InventoryQuantityOptionsPayload {
  quantityUnitId?: string | null;
  quantityUnitOverrides?: Record<string, number> | null;
  quantityUnitSize?: number | null;
  classification?: {
    unitId?: string | null;
    displayUnitId?: string | null;
    kind?: string | null;
    unitOverrides?: Record<string, number> | null;
    unitSize?: number | null;
  } | null;
}

export interface RetailInventoryPrepPayload extends InventoryQuantityOptionsPayload {
  levelId: string;
  quantity: number;
  reference?: string | null;
  note?: string | null;
  recipeId?: string | null;
}

export const submitRetailPrepBatch = async (
  payload: RetailInventoryPrepPayload,
): Promise<RetailInventoryLevel> => {
  const response = await callRetailApi(
    `${RETAIL_API_BASE}/inventory/prep`,
    LevelWithLedgerSchema,
    {
      method: "POST",
      body: JSON.stringify({
        levelId: payload.levelId,
        quantity: payload.quantity,
        reference: payload.reference ?? null,
        note: payload.note ?? null,
        recipeId: payload.recipeId ?? null,
        quantityUnitId: payload.quantityUnitId ?? null,
        quantityUnitOverrides: payload.quantityUnitOverrides ?? null,
        quantityUnitSize: payload.quantityUnitSize ?? null,
        classification: payload.classification ?? null,
      }),
    },
  );
  return { ...response.level };
};

export interface RetailInventoryWastePayload extends InventoryQuantityOptionsPayload {
  levelId: string;
  quantity: number;
  reason: string;
  note?: string | null;
  attachments?: string[] | null;
}

export const logRetailInventoryWaste = async (
  payload: RetailInventoryWastePayload,
): Promise<RetailInventoryLevel> => {
  const response = await callRetailApi(
    `${RETAIL_API_BASE}/inventory/waste`,
    LevelWithLedgerSchema,
    {
      method: "POST",
      body: JSON.stringify({
        levelId: payload.levelId,
        quantity: payload.quantity,
        reason: payload.reason,
        note: payload.note ?? null,
        attachments: payload.attachments ?? null,
        quantityUnitId: payload.quantityUnitId ?? null,
        quantityUnitOverrides: payload.quantityUnitOverrides ?? null,
        quantityUnitSize: payload.quantityUnitSize ?? null,
        classification: payload.classification ?? null,
      }),
    },
  );
  return { ...response.level };
};

export interface RetailInventoryTransferPayload {
  sourceLevelId: string;
  targetLevelId: string;
  quantity: number;
  reference?: string | null;
  note?: string | null;
  sourceOptions?: InventoryQuantityOptionsPayload;
  targetOptions?: InventoryQuantityOptionsPayload;
}

export const submitRetailInventoryTransfer = async (
  payload: RetailInventoryTransferPayload,
) => {
  const response = await callRetailApi(
    `${RETAIL_API_BASE}/inventory/transfer`,
    RetailInventoryTransferResponseSchema,
    {
      method: "POST",
      body: JSON.stringify({
        sourceLevelId: payload.sourceLevelId,
        targetLevelId: payload.targetLevelId,
        quantity: payload.quantity,
        reference: payload.reference ?? null,
        note: payload.note ?? null,
        source: payload.sourceOptions ?? undefined,
        target: payload.targetOptions ?? undefined,
      }),
    },
  );
  return response;
};

export interface RetailInventoryLedgerQuery {
  levelId?: string;
  limit?: number;
  signal?: AbortSignal;
}

export const fetchRetailInventoryLedger = async ({
  levelId,
  limit,
  signal,
}: RetailInventoryLedgerQuery): Promise<RetailInventoryLedgerEntry[]> => {
  const params = new URLSearchParams();
  if (levelId) {
    params.set("levelId", levelId);
  }
  if (typeof limit === "number" && Number.isFinite(limit)) {
    params.set("limit", String(limit));
  }

  const query = params.toString();
  const path =
    query.length > 0
      ? `${RETAIL_API_BASE}/inventory/ledger?${query}`
      : `${RETAIL_API_BASE}/inventory/ledger`;

  return callRetailApi(path, z.array(RetailInventoryLedgerEntrySchema), { signal });
};

export interface RetailCycleCountTask {
  id: string;
  levelId: string;
  productId: string;
  productName: string;
  productSku: string;
  locationId: string | null;
  locationName: string | null;
  status: "scheduled" | "in_progress" | "completed";
  requestedAt: string;
  note: string | null;
  expectedOnHand: number;
}

export interface RetailCycleCountPayload {
  levelIds: string[];
  note?: string | null;
}

export const scheduleRetailCycleCount = async (
  payload: RetailCycleCountPayload,
): Promise<RetailCycleCountTask[]> => {
  const tasks = await callRetailApi(
    `${RETAIL_API_BASE}/inventory/cycle-counts`,
    z.array(RetailCycleCountTaskSchema),
    {
      method: "POST",
      body: JSON.stringify({
        levelIds: payload.levelIds,
        note: payload.note ?? null,
      }),
    },
  );
  return tasks.map((task) => ({ ...task }));
};

export interface RetailTransactionInputItem {
  productId: string | null;
  variantId?: string | null;
  quantity: number;
  unitPriceCents: number;
  discountCents?: number;
  kind?: 'catalog' | 'service';
  taxPolicy?: 'default' | 'override' | 'exempt';
  taxRateOverride?: number | null;
}

export interface RetailTransactionInputTender {
  method: "cash" | "card" | "gift" | "store_credit";
  amountCents: number;
  externalReference?: string | null;
}

export interface RetailTransactionPayload {
  locationId?: string | null;
  registerId?: string | null;
  associateId?: string | null;
  channel?: "pos" | "bopis" | "ship";
  status?: "pending" | "completed" | "voided" | "refunded";
  currency?: string;
  metadata?: Record<string, unknown>;
  customerProfileId?: string | null;
  loyaltyEmail?: string | null;
  customerName?: string | null;
  items: RetailTransactionInputItem[];
  tenders: RetailTransactionInputTender[];
}

export const createRetailTransactionRecord = async (
  payload: RetailTransactionPayload,
): Promise<RetailTransaction> => {
  const transaction = await callRetailApi(
    `${RETAIL_API_BASE}/transactions`,
    RetailTransactionCreatedSchema,
    {
      method: "POST",
      body: JSON.stringify({
        ...payload,
        channel: payload.channel ?? "pos",
        status: payload.status ?? "completed",
        currency: payload.currency ?? "USD",
      }),
    },
  );
  return cloneTransaction(transaction);
};
